import React, {
	forwardRef,
	memo,
	useCallback,
	useEffect,
	useId as useReactId,
	useState,
	type KeyboardEvent,
} from 'react';
import { useUID } from 'react-uid';
import { type Jast, creators } from '@atlaskit/jql-ast';
import type { PopupComponentProps, ContentProps } from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import type { SelectedOrderByFields } from '@atlassian/jira-jql-builder-basic-order-by-select/src/types.tsx';
import OrderBySelect from '@atlassian/jira-jql-builder-basic-order-by-select/src/ui/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { JQLEditorUIWithErrorBoundary$data } from '@atlassian/jira-relay/src/__generated__/JQLEditorUIWithErrorBoundary.graphql';
import type {
	JiraJqlViewContext,
	JiraJqlScopeInput,
} from '@atlassian/jira-relay/src/__generated__/useFieldsQuery.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import OrderByButton from '../../../common/ui/order-by-trigger-button/index.tsx';
import { useActivePopupKey } from '../../../controllers/active-popup/index.tsx';
import { useFields } from '../../../services/use-fields/useFields.tsx';
import { getAstSortFields } from '../../../utils/get-ast-sort-fields/index.tsx';
import { useShouldRenderToParent } from '../../../controllers/feature-context.tsx';
import Footer from './footer/index.tsx';
import { compareSortFields, getNextSelectedOrderByFields, printJast } from './utils.tsx';

const useId = functionWithCondition(
	() => fg('jira-react-uid-to-use-id-migration'),
	useReactId,
	useUID,
);

export const ORDER_BY_DROPDOWN = 'ORDER_BY_DROPDOWN';

/**
 * Replaces the default container element that wraps the picker content
 * so we can control its css properties
 */
const CustomPopupComponent = forwardRef<HTMLDivElement, PopupComponentProps>(
	({ children, 'data-testid': testId, xcss: _xcss, ...props }, ref) => (
		// eslint-disable-next-line react/jsx-props-no-spreading
		<Box xcss={containerStyle} testId={testId} {...props} ref={ref}>
			{children}
		</Box>
	),
);

type OrderByProps = {
	/**
	 * The Jast representation of the current JQL
	 */
	ast: Jast;
	/**
	 * The JQL used when fetching available field picker options, this is sometimes different to
	 * the jql being built and can contain extra clauses, usually for adding a project scope to the
	 * query in order to restrict picker options
	 */
	contextJql: string;
	/**
	 * Called whenever the selected order by fields are updated, changed or removed. Will be given
	 * the latest jql as a string, typically this is used to trigger a new search within the app
	 */
	onChange: (jql: string) => void;
	/**
	 * hydrated data for the JQL query (from Relay)
	 */
	hydrationData: JQLEditorUIWithErrorBoundary$data;
	viewContext: JiraJqlViewContext | null;
	scope: JiraJqlScopeInput | null;
};

const OrderByPicker = ({
	ast,
	onChange,
	contextJql,
	hydrationData,
	viewContext,
	scope,
}: OrderByProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const shouldRenderToParent = useShouldRenderToParent();

	const [internalSelectedFields, setInternalSelectedFields] = useState(() =>
		getNextSelectedOrderByFields({ ast, hydrationData, selectedOrderByFields: [] }),
	);

	const firePickerAnalytics = useCallback(
		(isOpen: boolean, orderByFieldCount: number) => {
			fireUIAnalytics(createAnalyticsEvent({}), {
				action: 'clicked',
				actionSubject: 'dropdownMenu',
				actionSubjectId: 'orderByPicker',
				attributes: {
					isOpen,
					// So we can determine if the add field button needs to be
					// repositioned as long lists of fields mean it is needing to be
					// scrolled to
					orderByFieldCount,
				},
			});
		},
		[createAnalyticsEvent],
	);

	const [activePopupKey, { openPopup, closePopup }] = useActivePopupKey();

	const onOpen = useCallback(() => {
		openPopup(ORDER_BY_DROPDOWN);
		firePickerAnalytics(true, internalSelectedFields.length);
	}, [firePickerAnalytics, internalSelectedFields.length, openPopup]);

	const onClose = useCallback(() => {
		firePickerAnalytics(false, internalSelectedFields.length);
		closePopup(ORDER_BY_DROPDOWN);
	}, [closePopup, firePickerAnalytics, internalSelectedFields.length]);

	const isOpen = activePopupKey === ORDER_BY_DROPDOWN;

	const buttonId = useId();

	/**
	 * Popup does not want to close when users click browser back / forward buttons so manually
	 * compare the AST fields to the fields in the internal state, if they differ, the JQL was changed
	 * by an external action, so close the picker.
	 */
	useEffect(() => {
		const astSortFields = getAstSortFields(ast);

		const isInternalChange = compareSortFields(astSortFields, internalSelectedFields);

		if (!isInternalChange) {
			onClose();
		}
	}, [ast, internalSelectedFields, onClose]);

	/**
	 * Changes to sort fields can come from internal (user interacting with the picker) or external
	 * (jql is changed by other means such as clicking a filter or browser back button). This means
	 * the picker needs to handle both scenarios and update the UI accordingly. This is achieved by
	 * comparing the ast, hydration data and what is currently internally tracked and deciding which
	 * sort fields to set in the next state
	 */
	useEffect(() => {
		setInternalSelectedFields((currentInternalSelectedFields) =>
			getNextSelectedOrderByFields({
				ast,
				hydrationData,
				selectedOrderByFields: currentInternalSelectedFields,
			}),
		);
	}, [ast, hydrationData]);

	/**
	 * any currently selected field becomes an excluded field so that duplicates cannot be selected
	 * in the picker(s)
	 */
	const excludedFields = internalSelectedFields.map(
		(selctedOrderByField) => selctedOrderByField.jqlTerm,
	);

	/**
	 * each rendered basic-picker within OrderBySelect uses this to fetch the picker options
	 */
	const onLoadOptions = useFields({
		jql: contextJql,
		excludeFields: excludedFields,
		forClause: 'ORDER_BY',
		viewContext,
		scope,
	});

	/**
	 * attaches to a container element that wraps the picker and will prevent Esc key presses
	 * bubbling outside of the picker.
	 * This is needed for times when the picker might be rendered in a modal and we want
	 * users to be able to press Esc to close the picker, without also closing the modal.
	 */
	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLDivElement>) => {
			if (event.key === 'Escape') {
				isOpen && event.stopPropagation();
				onClose();
			}
		},
		[isOpen, onClose],
	);

	/**
	 * Called each time the OrderBySelect fields change, are added or removed.
	 * This handler will take the new fields object, use it to update the ast which is printed to a
	 * string and passed to the onChange callback to trigger a new search
	 */
	const handleOnChange = useCallback(
		(fields: SelectedOrderByFields) => {
			// If users have clicked the same option from a picker, no need to trigger a whole new
			// search
			const shouldUpdate = !compareSortFields(internalSelectedFields, fields);

			if (shouldUpdate) {
				// Set the internal state so changes a immediately visible (optimistic update)
				setInternalSelectedFields(fields);

				// Trigger a new search as a side effect this will then be handled in the useEffects
				if (ast.query) {
					ast.query.replaceOrderBy(
						creators.orderBy(
							fields.map((f) =>
								creators.orderByField(
									creators.field(f.jqlTerm),
									f.direction ? creators.orderByDirection(f.direction) : undefined,
								),
							),
						),
					);
					onChange(printJast(ast));
				}
			}
		},
		[ast, internalSelectedFields, onChange],
	);

	const handleOnClearAllFields = useCallback(() => {
		setInternalSelectedFields([]);

		if (ast.query) {
			ast.query.removeOrderBy();
			onChange(printJast(ast));
		}
	}, [ast, onChange]);

	const renderContent = useCallback(
		({ setInitialFocusRef }: ContentProps) => (
			<>
				<Box xcss={menuStyle} role="presentation" ref={setInitialFocusRef}>
					<OrderBySelect
						fields={internalSelectedFields}
						onLoadPickerOptions={onLoadOptions}
						onChange={handleOnChange}
					/>
				</Box>
				{Boolean(internalSelectedFields.length) && <Footer onClear={handleOnClearAllFields} />}
			</>
		),
		[internalSelectedFields, onLoadOptions, handleOnChange, handleOnClearAllFields],
	);

	return (
		<Box onKeyDown={handleKeyDown} role="presentation">
			<Popup
				role="dialog"
				titleId={buttonId}
				isOpen={isOpen}
				onClose={onClose}
				autoFocus={false}
				shouldRenderToParent={shouldRenderToParent}
				placement="bottom-start"
				messageId="jql-builder-basic.ui.jql-editor.order-by-picker.popup"
				messageType="transactional"
				popupComponent={CustomPopupComponent}
				content={renderContent}
				trigger={(triggerProps) => (
					<OrderByButton
						isSelected={isOpen}
						buttonId={buttonId}
						triggerProps={triggerProps}
						numberOfFields={internalSelectedFields.length}
						onClick={isOpen ? onClose : onOpen}
					/>
				)}
			/>
		</Box>
	);
};
export default memo(OrderByPicker);

const containerStyle = xcss({
	zIndex: 'layer',
	backgroundColor: 'elevation.surface.overlay',
	boxShadow: 'elevation.shadow.overlay',
	width: '300px',
	borderRadius: 'radius.small',
});

const menuStyle = xcss({
	maxHeight: '284px',
	padding: 'space.150',
	paddingLeft: 'space.150',
	overflow: 'auto',
});
